/* ============================================================================
   assets/boost-contact-form.js   (v4, 2026-07-18)

   BOOST CUSTOMISATION 2026-07-18 (CONTACT FORMS):
   Shared behaviour for BOTH contact surfaces:
     - sections/boost-contact-form.liquid       (Contact Us page)
     - snippets/boost-hub-contact-form.liquid   (My Account hub)

   v4 CHANGES (from the T-00847..T-00903 tester notes):
     - T-00864 / T-00901 / T-00854: validation now GENUINELY BLOCKS submission.
       The form cannot post while a required field is empty or the email is
       malformed. This is the load-bearing fix; everything else is secondary.
     - Date picker REMOVED entirely (T-00858 / T-00893). No date logic remains.
     - Spam honeypot + timer REMOVED (T-00872): they were tripping Shopify's
       hCaptcha challenge. The form is now a plain native contact form.
     - Topic no longer swaps a "clever" prompt (T-00896). The form box stays
       clean (dropdown + Enquiry details); the standing Customer Care box lists
       every team with its phone option, so no per-team detail is injected.
     - Industry topic still reveals the picker (now for ALL customers, no tag),
       and the emailed delta uses the exact labels Philip specified (T-00884):
         Full List Before Change / Add These / Remove These / Full List After Change
     - VIP topic + logged in: the Organisation field is not required and is
       hidden, because we already know it (T-00871 / T-00895).
   ========================================================================== */

(function () {
  'use strict';

  /* ---------------------------------------------------------------------- */
  /* Hidden-field helpers                                                   */
  /* ---------------------------------------------------------------------- */

  function setHidden(form, name, value) {
    var el = form.querySelector('input[type="hidden"][name="' + name + '"]');
    if (!el) {
      el = document.createElement('input');
      el.type = 'hidden';
      el.name = name;
      form.appendChild(el);
    }
    el.value = value;
  }

  function removeHidden(form, name) {
    var el = form.querySelector('input[type="hidden"][name="' + name + '"]');
    if (el && el.parentNode) el.parentNode.removeChild(el);
  }

  function splitList(str) {
    if (!str) return [];
    return str.split('|').map(function (s) { return s.trim(); })
      .filter(function (s) { return s.length > 0; });
  }

  function parseTopics(root) {
    var node = root.querySelector('[data-boost-cf-topics]');
    if (!node) return {};
    try { return JSON.parse(node.textContent || '{}'); } catch (e) { return {}; }
  }

  /* ---------------------------------------------------------------------- */
  /* Inline validation                                                      */
  /* ---------------------------------------------------------------------- */

  function fieldOf(input) { return input.closest('.boost-cf-field'); }

  function messageFor(input) {
    if (input.validity.valueMissing) {
      return input.getAttribute('data-msg-required') || 'This one is required.';
    }
    if (input.type === 'email' && input.validity.typeMismatch) {
      return 'Please tell us your email, so we can reply.';
    }
    return 'Please check this value.';
  }

  function showError(input) {
    var field = fieldOf(input);
    if (!field) return;
    var msg = field.querySelector('.boost-cf-error');
    if (!msg) {
      msg = document.createElement('p');
      msg.className = 'boost-cf-error';
      msg.setAttribute('aria-live', 'polite');
      field.appendChild(msg);
    }
    msg.textContent = messageFor(input);
    field.classList.add('boost-cf-field--invalid');
    input.setAttribute('aria-invalid', 'true');
  }

  function clearError(input) {
    var field = fieldOf(input);
    if (!field) return;
    var msg = field.querySelector('.boost-cf-error');
    if (msg) msg.textContent = '';
    field.classList.remove('boost-cf-field--invalid');
    input.removeAttribute('aria-invalid');
  }

  /* A field counts if it is required and its wrapper is not hidden. We must NOT
     use offsetParent/offsetHeight to judge visibility: the theme's
     plugin-selects.js hides the native <select> (replacing it with a .bv_mainselect
     widget), so a perfectly active topic select has offsetParent === null. Judging
     by the element's own rendered box therefore silently SKIPPED the required
     topic select, which is why the form submitted with only the textarea checked
     (T-00864/901). Visibility is decided by the field wrapper's [hidden] attribute,
     which is our own disclosure mechanism. */
  function isActive(input) {
    if (!input.hasAttribute('required')) return false;
    var field = fieldOf(input);
    if (field && field.hasAttribute('hidden')) return false;
    return true;
  }

  function validateOne(input) {
    if (!isActive(input)) { clearError(input); return true; }
    if (input.checkValidity()) { clearError(input); return true; }
    showError(input);
    return false;
  }

  /* ---------------------------------------------------------------------- */
  /* Industry delta (exact labels per T-00884)                              */
  /* ---------------------------------------------------------------------- */

  var IND_BEFORE = 'contact[Industries - Full List Before Change]';
  var IND_ADD    = 'contact[Industries - Add These]';
  var IND_REMOVE = 'contact[Industries - Remove These]';
  var IND_AFTER  = 'contact[Industries - Full List After Change]';

  function clearIndustryDelta(form) {
    removeHidden(form, IND_BEFORE);
    removeHidden(form, IND_ADD);
    removeHidden(form, IND_REMOVE);
    removeHidden(form, IND_AFTER);
  }

  function computeDelta(picker) {
    var current = splitList(picker.getAttribute('data-current'));
    var boxes = picker.querySelectorAll('input[type="checkbox"][data-industry]');
    var selected = [];
    Array.prototype.forEach.call(boxes, function (b) {
      if (b.checked) selected.push(b.getAttribute('data-industry'));
    });
    return {
      before: current,
      selected: selected,
      add: selected.filter(function (x) { return current.indexOf(x) === -1; }),
      remove: current.filter(function (x) { return selected.indexOf(x) === -1; })
    };
  }

  function buildIndustryDelta(form, picker) {
    var d = computeDelta(picker);
    setHidden(form, IND_BEFORE, d.before.length ? d.before.join(', ') : 'None');
    setHidden(form, IND_ADD,    d.add.length ? d.add.join(', ') : 'None');
    setHidden(form, IND_REMOVE, d.remove.length ? d.remove.join(', ') : 'None');
    setHidden(form, IND_AFTER,  d.selected.length ? d.selected.join(', ') : 'None');
  }

  function renderIndustrySummary(picker) {
    var out = picker.querySelector('[data-boost-cf-industry-summary]');
    if (!out) return;
    var d = computeDelta(picker);
    if (!d.add.length && !d.remove.length) {
      out.innerHTML = '<span class="boost-cf-ind-none">No changes selected yet.</span>';
      return;
    }
    var html = '';
    if (d.add.length) html += '<span class="boost-cf-ind-add"><strong>Add:</strong> ' + d.add.join(', ') + '</span>';
    if (d.remove.length) html += '<span class="boost-cf-ind-remove"><strong>Remove:</strong> ' + d.remove.join(', ') + '</span>';
    out.innerHTML = html;
  }

  /* ---------------------------------------------------------------------- */
  /* Topic disclosure: industry picker + org requirement                    */
  /* ---------------------------------------------------------------------- */

  function applyTopic(ctx) {
    var value = ctx.topic ? ctx.topic.value : '';
    var meta = ctx.topics[value] || {};
    var chosen = value !== '';

    /* Progressive reveal (hub): elements marked data-boost-cf-after-topic stay
       hidden until ANY topic is chosen, so the resting form is just the dropdown
       and the Customer Care card below stays above the fold. Keyed on "a topic is
       chosen", not a specific topic. */
    if (ctx.afterTopic && ctx.afterTopic.length) {
      Array.prototype.forEach.call(ctx.afterTopic, function (el) {
        el.hidden = !chosen;
      });
    }

    /* Industry picker (hub only). */
    if (ctx.industryField) {
      var wantInd = !!meta.industries;
      ctx.industryField.hidden = !wantInd;
      if (wantInd) renderIndustrySummary(ctx.industryField);
      else clearIndustryDelta(ctx.form);
    }

    /* VIP + logged in: Organisation is known, so don't ask for it. */
    if (ctx.orgField) {
      var hideOrg = !!meta.hide_org && ctx.loggedIn;
      ctx.orgField.hidden = hideOrg;
      var orgInput = ctx.orgField.querySelector('input');
      if (orgInput) {
        if (hideOrg) { orgInput.removeAttribute('required'); clearError(orgInput); }
        else { orgInput.setAttribute('required', 'required'); }
      }
    }
  }

  /* ---------------------------------------------------------------------- */
  /* Submit: BLOCK on invalid                                               */
  /* ---------------------------------------------------------------------- */

  function onSubmit(ctx, event) {
    var form = ctx.form;

    /* Re-query required fields at submit time (topic disclosure may have
       toggled `required` on the Organisation field). */
    var required = form.querySelectorAll('[required]');
    var firstBad = null;
    Array.prototype.forEach.call(required, function (input) {
      if (!validateOne(input) && !firstBad) firstBad = input;
    });

    if (firstBad) {
      event.preventDefault();
      event.stopImmediatePropagation();
      var identity = firstBad.closest('[data-boost-cf-identity-fields]');
      if (identity && identity.hidden) openIdentity(ctx);
      focusField(firstBad);
      firstBad.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return false;
    }

    /* Industry delta, only when that topic is live. */
    if (ctx.industryField && !ctx.industryField.hidden) {
      buildIndustryDelta(form, ctx.industryField);
    } else {
      clearIndustryDelta(form);
    }

    /* Hub only: keep a single-collection VIP on the hub for the return trip. */
    if (form.hasAttribute('data-boost-cf-hub')) {
      try { sessionStorage.setItem('boost_hub_vip_redirected', '1'); } catch (e) {}
    }

    /* Analytics. */
    var topicValue = ctx.topic ? ctx.topic.value : '';
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'boost_contact_submitted',
        boost_form: form.getAttribute('data-boost-cf-source') || 'contact',
        boost_topic: topicValue
      });
    } catch (e) {}
    try {
      window.dispatchEvent(new CustomEvent('boost:contact_submitted', {
        detail: { source: form.getAttribute('data-boost-cf-source') || 'contact', topic: topicValue }
      }));
    } catch (e) {}

    /* valid: let the native contact form post. */
    return true;
  }

  /* ---------------------------------------------------------------------- */
  /* Select-plugin focus + identity toggle                                  */
  /* ---------------------------------------------------------------------- */

  function focusField(input) {
    if (input.tagName === 'SELECT') {
      var field = fieldOf(input);
      var widget = field ? field.querySelector('.bv_mainselect') : null;
      if (widget) {
        var t = widget.querySelector('.bv_atual') || widget;
        try { t.focus(); } catch (e) {}
        return;
      }
    }
    try { input.focus(); } catch (e) {}
  }

  function openIdentity(ctx) {
    if (!ctx.identityFields) return;
    ctx.identityFields.hidden = false;
    if (ctx.identitySummary) ctx.identitySummary.hidden = true;
    if (ctx.identityToggle) ctx.identityToggle.setAttribute('aria-expanded', 'true');
  }

  /* ---------------------------------------------------------------------- */
  /* Init                                                                   */
  /* ---------------------------------------------------------------------- */

  function init(root) {
    if (root.hasAttribute('data-boost-cf-ready')) return;
    root.setAttribute('data-boost-cf-ready', '');

    var form = root.querySelector('form');
    if (!form) return;

    var ctx = {
      root: root,
      form: form,
      loggedIn: root.hasAttribute('data-boost-cf-logged-in'),
      topics: parseTopics(root),
      topic: form.querySelector('[data-boost-cf-topic]'),
      industryField: form.querySelector('[data-boost-cf-industries]'),
      afterTopic: form.querySelectorAll('[data-boost-cf-after-topic]'),
      orgField: form.querySelector('[data-boost-cf-org-field]'),
      identityFields: form.querySelector('[data-boost-cf-identity-fields]'),
      identitySummary: form.querySelector('[data-boost-cf-identity-summary]'),
      identityToggle: form.querySelector('[data-boost-cf-identity-toggle]')
    };

    /* Our inline messages replace the browser bubble. */
    form.setAttribute('novalidate', 'novalidate');

    if (ctx.topic) {
      var onTopicChange = function () { applyTopic(ctx); clearError(ctx.topic); };
      /* The theme's plugin-selects.js hides the native <select> and only calls
         its inline onchange PROPERTY (never dispatches a change event). Liquid
         renders an onchange ATTRIBUTE so the plugin's guard passes; we assign
         the property here. addEventListener stays for mobile (plugin skips it). */
      ctx.topic.addEventListener('change', onTopicChange);
      ctx.topic.onchange = onTopicChange;
      applyTopic(ctx);
    }

    if (ctx.industryField) {
      ctx.industryField.addEventListener('change', function (e) {
        if (e.target && e.target.matches('input[type="checkbox"][data-industry]')) {
          renderIndustrySummary(ctx.industryField);
        }
      });
      renderIndustrySummary(ctx.industryField);
    }

    if (ctx.identityToggle) {
      ctx.identityToggle.addEventListener('click', function () {
        openIdentity(ctx);
        var first = ctx.identityFields ? ctx.identityFields.querySelector('input') : null;
        if (first) first.focus();
      });
    }

    /* Live per-field validation. */
    Array.prototype.forEach.call(form.querySelectorAll('input, select, textarea'), function (input) {
      input.addEventListener('blur', function () {
        if (input.value !== '' || input.getAttribute('aria-invalid') === 'true') validateOne(input);
      });
      input.addEventListener('input', function () {
        if (input.getAttribute('aria-invalid') === 'true') validateOne(input);
      });
    });

    /* CAPTURE-PHASE submit handler (T-00864/901 root cause).
       The theme's own validator (custom-async.js `validator_run`) only checks
       input + textarea for the disabled-button gate and IGNORES <select>, so once
       a topic is chosen its gate stops blocking even with an empty required
       textarea. To be authoritative regardless of the theme, we listen in the
       CAPTURE phase (runs before any bubble-phase theme handler and before the
       native submit) and, on any invalid field, preventDefault +
       stopImmediatePropagation so nothing downstream can submit the form. */
    form.addEventListener('submit', function (e) { onSubmit(ctx, e); }, true);

    var success = root.querySelector('[data-boost-cf-success]');
    if (success) {
      success.setAttribute('tabindex', '-1');
      try { success.focus({ preventScroll: true }); } catch (e) {}
      success.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  function initAll() {
    document.querySelectorAll('[data-boost-cf]').forEach(function (r) { init(r); });
  }

  /* Deep-link: hub "Change my industries" jumps to the form + selects a topic. */
  document.addEventListener('click', function (e) {
    if (!e.target || typeof e.target.closest !== 'function') return;
    var link = e.target.closest('[data-boost-cf-jump]');
    if (!link) return;
    var root = document.querySelector(link.getAttribute('data-boost-cf-jump'));
    if (!root) return;
    e.preventDefault();
    var select = root.querySelector('[data-boost-cf-topic]');
    var want = link.getAttribute('data-boost-cf-jump-topic');
    if (select && want) {
      select.value = want;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
    root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (select) { try { select.focus({ preventScroll: true }); } catch (e2) {} }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-boost-cf]').forEach(function (r) { init(r); });
  });
})();
